import { TextField } from '@mui/material';
import React, { SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../../../shared/functions/Functions';
import { IReportingUpdate, ISelectedEvent } from '../../ReportingCampaignCard';
import CardLayout from '../../../../../../shared/components/card/CardLayout';
import TooltipOverlay from '../../../../../../shared/components/tooltip/Tooltip';
import { EditableCopyField } from '../../../../../../shared/components/EditableCopyField';
import { ErrorToast, SuccessToast } from '../../../../../../utils/toasters';
import { ManageService } from '../../../../../brand/campaign/manage/service';

interface IProps {
    updateData: IReportingUpdate;
    setUpdateData: React.Dispatch<SetStateAction<IReportingUpdate>>;
    selectedOfferItem: ISelectedEvent;
}

const ReportingLeftCard = ({ selectedOfferItem, updateData, setUpdateData }: IProps) => {
    const { t } = useTranslation();
    let { isVerified, isRejected, status } = selectedOfferItem?.event ?? {};
    const isAdditional = selectedOfferItem?.offerItem?.additionalPost;
    if (isAdditional) {
        isVerified = selectedOfferItem?.offerItem?.isVerified;
        isRejected = selectedOfferItem?.offerItem?.isRejected;
        status = 'reported';
    }

    const eventId = selectedOfferItem?.event?.id;

    const changePostingLink = (value: string) => {
        setUpdateData(prev => ({ ...prev, postingLink: value }));
    };

    const updatePostingLink = async () => {
        if (!eventId) return false;
        try {
            const response = await ManageService.updatePostingLink(eventId, updateData?.postingLink ?? '');
            if (response) {
                SuccessToast(t('general.toasts.successUpdated'));
                return true;
            }
        } catch (error) {
            ErrorToast(error);
        }
        return false;
    };

    return (
        <div className="col-xl-3">
            <CardLayout classCard="card mb-3">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <img
                            src={selectedOfferItem?.offerItem?.socialPlatform?.path}
                            alt={selectedOfferItem?.offerItem?.socialPlatform?.name}
                        />
                        <div className="d-flex flex-column ml-2">
                            <span>
                                {isAdditional && 'Additional '}
                                {t(`general.platforms.${selectedOfferItem?.offerItem?.socialPlatform?.name}`)}{' '}
                                {!isAdditional && selectedOfferItem?.offerItem?.nbPosts
                                    ? ` ${selectedOfferItem?.event?.platformPosition ?? '-'}/${
                                          selectedOfferItem?.offerItem?.nbPosts
                                      }`
                                    : ''}
                            </span>
                            <p className="mb-0 text-muted">
                                {formatDate(
                                    updateData?.postingDate
                                        ? updateData?.postingDate
                                        : selectedOfferItem?.event?.postingDate
                                )}
                            </p>
                        </div>
                    </div>
                    {status === 'reported' && (
                        <TooltipOverlay
                            content={
                                isVerified
                                    ? t('general.campaign_status.approved')
                                    : isRejected
                                    ? t('general.campaign_status.rejected')
                                    : t('brand.campaign.measure.being_verified')
                            }
                        >
                            {isVerified ? (
                                <img src="/icons/approved-icon.svg" alt="Approved icon" />
                            ) : isRejected ? (
                                <img src="/icons/not-approved-icon.svg" alt="Not approved icon" />
                            ) : (
                                <img src="/icons/being-verified-icon.svg" alt="Being verified icon" />
                            )}
                        </TooltipOverlay>
                    )}
                </div>
                {isRejected && !!selectedOfferItem?.event?.rejectReason?.length && (
                    <div className="mt-3 border-danger rounded p-2">
                        <h6 className="mb-0">{t('influencer.reporting.rejection_reason')}</h6>
                        <span className="text-muted">{selectedOfferItem?.event?.rejectReason}</span>
                    </div>
                )}
            </CardLayout>

            {!isAdditional &&
                (eventId ? (
                    <EditableCopyField
                        label={t('influencer.reporting.postingLink')}
                        value={updateData?.postingLink}
                        onChange={changePostingLink}
                        onSave={updatePostingLink}
                        className="mb-3 mb-xl-0"
                    />
                ) : (
                    <TextField
                        fullWidth
                        name="postingLink"
                        value={updateData?.postingLink ?? ''}
                        onChange={e => changePostingLink(e.target.value)}
                        label={t('influencer.reporting.postingLink')}
                        type="text"
                        className="mb-3 mb-xl-0"
                        variant="outlined"
                        size="small"
                    />
                ))}
        </div>
    );
};

export default ReportingLeftCard;
